import { useEffect, useState, useCallback } from 'react';
import { supabase, retryOperation } from '../lib/supabase';
import { useAuth } from './useAuth';
import type { UserPlant } from '../types/garden';

export function useUserGarden() {
  const [userGarden, setUserGarden] = useState<UserPlant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();

  const fetchUserGarden = useCallback(async (retryCount = 0) => {
    try {
      // If no user, don't attempt to fetch
      if (!user) {
        setUserGarden([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      const { data, error: fetchError } = await retryOperation(() =>
        supabase
          .from('user_gardens')
          .select(`
            id,
            user_id,
            plant_id,
            nickname,
            planting_date,
            notes,
            sow_indoors_start_date,
            sow_indoors_end_date,
            direct_sow_start_date,
            direct_sow_end_date,
            transplant_start_date,
            transplant_end_date,
            success_notes,
            varieties_notes,
            created_at,
            updated_at,
            plant:plants (
              id,
              name,
              scientific_name,
              description,
              sunlight_requirements,
              water_requirements,
              image_url,
              frost_tolerance,
              days_to_transplant_min,
              days_to_transplant_max,
              time_seed_to_maturity_max,
              time_seed_to_maturity_min,
              direct_sow_cold_hardy_days_before_frost_max,
              transplant_cold_hardy_days_before_frost_max,
              harvest_window_max
            )
          `)
          .eq('user_id', user.id)
          .order('plant(name)', { ascending: true })
      );

      if (fetchError) throw fetchError;
      
      setUserGarden(data || []);
      setError(null);
    } catch (err) {
      console.error('Error fetching garden plants:', err);
      if (retryCount < 3) {
        // Exponential backoff
        const delay = Math.pow(2, retryCount) * 1000;
        setTimeout(() => fetchUserGarden(retryCount + 1), delay);
      } else {
        setError(err instanceof Error ? err.message : 'An error occurred');
        setUserGarden([]); // Reset plants on error
      }
    } finally {
      setIsLoading(false);
    }
  }, [user]); // Only re-run when user changes

  useEffect(() => {
    fetchUserGarden();
  }, [fetchUserGarden]);

  const forceRefreshGarden = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Clear Supabase cache if possible
      if (typeof supabase.removeAllSubscriptions === 'function') {
        supabase.removeAllSubscriptions();
      }
      
      // Force reload from server
      const { data, error: fetchError } = await supabase
        .from('user_gardens')
        .select(`
          id,
          user_id,
          plant_id,
          nickname,
          planting_date,
          notes,
          sow_indoors_start_date,
          sow_indoors_end_date,
          direct_sow_start_date,
          direct_sow_end_date,
          transplant_start_date,
          transplant_end_date,
          success_notes,
          varieties_notes,
          created_at,
          updated_at,
          plant:plants (
            id,
            name,
            scientific_name,
            description,
            sunlight_requirements,
            water_requirements,
            image_url,
            frost_tolerance,
            days_to_transplant_min,
            days_to_transplant_max,
            time_seed_to_maturity_max,
            time_seed_to_maturity_min,
            direct_sow_cold_hardy_days_before_frost_max,
            transplant_cold_hardy_days_before_frost_max,
            harvest_window_max
          )
        `)
        .eq('user_id', user.id)
        .order('plant(name)', { ascending: true })
        .options({ cache: 'no-store' });
        
      if (fetchError) throw fetchError;
      
      setUserGarden(data || []);
    } catch (err) {
      console.error('Error fetching garden data:', err);
      setError('Failed to load garden plants');
    } finally {
      setIsLoading(false);
    }
  };

  const addPlant = async (plantId: string) => {
    if (!user) {
      throw new Error('Must be logged in to add plants to garden');
    }

    try {
      const { data, error } = await retryOperation(() =>
        supabase
          .from('user_gardens')
          .insert({
            user_id: user.id,
            plant_id: plantId,
            planting_date: new Date().toISOString()
          })
          .select()
      );

      if (error) throw error;
      
      // Refresh garden data
      fetchUserGarden();
      
      return data;
    } catch (err) {
      console.error('Error adding plant to garden:', err);
      throw err;
    }
  };

  const updatePlant = async (plantId: string, updates: Partial<UserPlant>) => {
    if (!user) {
      throw new Error('Must be logged in to update plants');
    }

    try {
      const { data, error } = await retryOperation(() =>
        supabase
          .from('user_gardens')
          .update(updates)
          .eq('id', plantId)
          .eq('user_id', user.id)
          .select()
      );

      if (error) throw error;
      
      // Refresh garden data
      fetchUserGarden();
      
      return data;
    } catch (err) {
      console.error('Error updating plant:', err);
      throw err;
    }
  };

  const deletePlant = async (plantId: string) => {
    if (!user) {
      throw new Error('Must be logged in to delete plants');
    }

    try {
      setIsLoading(true);
      setError(null);
      
      const { error } = await retryOperation(() =>
        supabase
          .from('user_gardens')
          .delete()
          .eq('id', plantId)
          .eq('user_id', user.id)
      );

      if (error) throw error;
      
      // Update local state to remove the deleted plant
      setUserGarden(prev => prev.filter(p => p.id !== plantId));
    } catch (err) {
      console.error('Error deleting plant:', err);
      setError('Failed to delete plant. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  // New function to recalculate garden dates
  const recalculateGardenDates = async () => {
    if (!user) {
      throw new Error('Must be logged in to recalculate garden dates');
    }

    setIsLoading(true);
    setError(null);

    try {
      // Call the RPC function to recalculate garden dates
      const { error: recalcError } = await supabase
        .rpc('recalculate_garden_dates', { 
          user_id_param: user.id 
        });

      if (recalcError) throw recalcError;
      
      // Refresh garden data
      await fetchUserGarden();
      
      return true;
    } catch (err) {
      console.error('Error recalculating garden dates:', err);
      setError('Failed to recalculate garden dates. Please try again.');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return { 
    plants: userGarden, 
    loading: isLoading, 
    error,
    addToGarden: addPlant,
    updatePlant,
    deletePlant,
    forceRefreshGarden,
    recalculateGardenDates,
    refetch: fetchUserGarden
  };
}